import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Search, X, Package, ChevronRight, TrendingDown, AlertTriangle,
  Send, Sliders,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STATUS_META, CATEGORIES, UNITS, fmtQty, inr } from "./lib";
import PartFormDialog from "./components/PartFormDialog";
import StockAdjustDialog from "./components/StockAdjustDialog";
import DirectInwardDialog from "./components/DirectInwardDialog";

const SAVED_VIEWS = [
  { key: "",              label: "All parts" },
  { key: "in_stock",      label: "In Stock" },
  { key: "low_stock",     label: "Low Stock" },
  { key: "out_of_stock",  label: "Out of Stock" },
  { key: "reserved",      label: "Reserved" },
  { key: "inactive",      label: "Inactive" },
];

export default function StockList() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const [view, setView] = useState(sp.get("view") || "");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [adjustingPart, setAdjustingPart] = useState(null);
  const [inwardOpen, setInwardOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (view) params.stock_status = view;
    if (q.trim()) params.q = q.trim();
    if (category) params.category = category;
    if (brand) params.brand = brand;
    api.get("/spares/parts", { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, view, category, brand]);

  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (view) next.set("view", view); else next.delete("view");
    setSp(next, { replace: true });
     
  }, [view]);

  const brands = useMemo(() => Array.from(new Set(rows.map((r) => r.brand).filter(Boolean))).sort(), [rows]);

  return (
    <div className="space-y-4" data-testid="spare-stock-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Stock</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            {rows.length} part{rows.length === 1 ? "" : "s"} — the numbers below are live.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setInwardOpen(true)}
            data-testid="spare-stock-receive-btn"
            className="h-10 px-4 rounded-md border border-[#12B76A]/40 text-[#0F9256] hover:bg-[#F1FAEE] text-[12.5px] font-semibold inline-flex items-center gap-1.5"
          >
            <Plus size={13} /> Receive Stock
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="spare-stock-add-btn"
            className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
          >
            <Plus size={13} /> Add Part
          </button>
        </div>
      </header>

      {/* Saved views + filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1 overflow-x-auto">
          {SAVED_VIEWS.map((v) => (
            <button
              key={v.key || "all"}
              onClick={() => setView(v.key)}
              data-testid={`spare-view-${v.key || "all"}`}
              className={`h-8 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
                view === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, part number, brand, category…"
            data-testid="spare-stock-search"
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="spare-stock-category-filter"
          className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[12.5px] bg-white focus:border-[#457b9d] outline-none"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={brand} onChange={(e) => setBrand(e.target.value)}
          data-testid="spare-stock-brand-filter"
          className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[12.5px] bg-white focus:border-[#457b9d] outline-none"
        >
          <option value="">All brands</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {/* Table */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="spare-stock-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <Package size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No parts match this filter</div>
            <p className="text-[12px] text-[#667085] mt-1">Add a part to start tracking stock.</p>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="spare-stock-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                    <th className="px-5 py-2.5">Part</th>
                    <th className="px-3 py-2.5">Brand</th>
                    <th className="px-3 py-2.5">Category</th>
                    <th className="px-3 py-2.5 text-right">Stock</th>
                    <th className="px-3 py-2.5 text-right">Reserved</th>
                    <th className="px-3 py-2.5 text-right">Available</th>
                    <th className="px-3 py-2.5 text-right">Reorder</th>
                    <th className="px-3 py-2.5 text-right">Selling</th>
                    <th className="px-3 py-2.5">Location</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-5 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.in_stock;
                    return (
                      <tr
                        key={r.id}
                        className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC]"
                        data-testid={`spare-row-${r.id}`}
                      >
                        <td className="px-5 py-3">
                          <Link
                            to={`/spare-parts/stock/${r.id}`}
                            className="text-[13px] font-semibold text-[#1d3557] hover:text-[#e63946]"
                          >
                            {r.name}
                          </Link>
                          {r.part_number && (
                            <div className="text-[11px] font-mono text-[#667085]">{r.part_number}</div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-[#667085]">{r.brand || "—"}</td>
                        <td className="px-3 py-3 text-[#667085]">{r.category || "—"}</td>
                        <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtQty(r.stock_qty, r.unit)}</td>
                        <td className="px-3 py-3 text-right font-mono tabular-nums text-[#667085]">{r.reserved_qty || 0}</td>
                        <td className="px-3 py-3 text-right font-mono tabular-nums font-bold">{r.available_qty}</td>
                        <td className="px-3 py-3 text-right text-[#667085]">{r.reorder_level || "—"}</td>
                        <td className="px-3 py-3 text-right font-mono tabular-nums">{inr(r.selling_price)}</td>
                        <td className="px-3 py-3 text-[#667085] text-[11.5px]">{r.storage_location || "—"}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setAdjustingPart(r)}
                            data-testid={`spare-adjust-${r.id}`}
                            className="h-7 px-2.5 rounded-md border border-[#E4E7EC] text-[11px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] mr-1"
                          >
                            Adjust
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingPart(r)}
                            data-testid={`spare-edit-${r.id}`}
                            className="h-7 px-2.5 rounded-md border border-[#E4E7EC] text-[11px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <ul className="md:hidden divide-y divide-[#F1FAEE]">
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.in_stock;
                return (
                  <li key={r.id}>
                    <Link
                      to={`/spare-parts/stock/${r.id}`}
                      className="block p-4 hover:bg-[#F7F9FC]"
                      data-testid={`spare-card-${r.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1d3557] truncate">{r.name}</div>
                          <div className="text-[11px] text-[#667085]">{r.brand || ""} · {r.category || ""}</div>
                          {r.part_number && (
                            <div className="text-[10.5px] font-mono text-[#98A2B3]">{r.part_number}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-[16px] font-bold font-mono tabular-nums text-[#1d3557]">{r.available_qty}</div>
                          <div className="text-[10px] text-[#667085] uppercase tracking-wider">available</div>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider ${meta.chip}`}>
                          {meta.label}
                        </span>
                        {r.selling_price ? <span className="text-[11px] font-mono text-[#667085]">{inr(r.selling_price)}</span> : null}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      <PartFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => { setCreateOpen(false); load(); }}
      />
      <PartFormDialog
        open={!!editingPart}
        initial={editingPart}
        onClose={() => setEditingPart(null)}
        onSaved={() => { setEditingPart(null); load(); }}
      />
      <StockAdjustDialog
        open={!!adjustingPart}
        part={adjustingPart}
        onClose={() => setAdjustingPart(null)}
        onSaved={() => { setAdjustingPart(null); load(); }}
      />
      <DirectInwardDialog
        open={inwardOpen}
        onClose={() => setInwardOpen(false)}
        onSaved={() => { setInwardOpen(false); load(); }}
      />
    </div>
  );
}
